import * as React from 'react';
import { Stack, Link, Text, Button } from '@withjoy/joykit';
import { TrackingFunc } from '@withjoy/web-core';
import { AuthButton, Input } from '..';
import { styles, EnhancedStack, InputStack, HelpText, StyledLink } from './styles';
import { utils } from '../../utils';
import { buildReactStateHandler } from '@lib';
import { CurrentForm } from '../..';
import { handleInputErrorsType, AuthField } from '../../../../../Authenticate/types';

interface AuthPanelProps {
  handleOnInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  handleInputErrors: handleInputErrorsType;
  email: AuthField;
  password: AuthField;
  authType: AuthType;
  handleOAuth: (e: React.MouseEvent<HTMLElement>) => void;
  error: string;
  handleFormChange: (currentForm: CurrentForm) => void;
  track: TrackingFunc;
}

export type AuthType = 'login' | 'signup';

interface AuthPanelState {
  emailFocused: boolean;
  passwordFocused: boolean;
  isSubmitting: boolean;
}

const stateHandler = buildReactStateHandler<AuthPanelState>();

export class AuthPanel extends React.Component<AuthPanelProps, AuthPanelState> {
  readonly state: AuthPanelState = {
    emailFocused: true,
    passwordFocused: true,
    isSubmitting: false
  };

  authText = {
    login: {
      buttonText: 'Login',
      heroText: 'Log into Joy.  🎉',
      emailText: 'Enter your email',
      passwordText: 'Enter your password',
      helpText: () => (
        <HelpText color="#a9a9a9" component="p" variant="body">
          Don’t have an account? <Link onClick={() => this.props.handleFormChange('signup')}>Create Account</Link>
        </HelpText>
      )
    },
    signup: {
      buttonText: 'Create Account',
      heroText: 'Create your Joy account  🎉',
      emailText: 'Enter your email',
      passwordText: 'Create a password',
      helpText: () => (
        <HelpText color="#a9a9a9" component="p" variant="body">
          Already have an account? <Link onClick={() => this.props.handleFormChange('login')}>Login Here</Link>
        </HelpText>
      )
    }
  };

  componentDidUpdate(prevProps: AuthPanelProps) {
    if (this.props !== prevProps) {
      this.setState({ isSubmitting: false });
    }
  }

  handleFocus = (focused: boolean, target: string) => {
    this.setState(
      stateHandler(draft => {
        if (this.props.track) {
          const action = `${target.charAt(0).toUpperCase()}${target.slice(1)}`;
          this.props.track({ action, actionType: 'click', label: this.props.authType });
        }
        draft[`${target}Focused`] = true;
      })
    );
  };

  handleBlur = (focused: boolean, target: string) => {
    this.setState(
      stateHandler(draft => {
        draft[`${target}Focused`] = false;
      })
    );
  };

  isSubmitDisabled = () => {
    const { email, password, authType } = this.props;
    return utils.isSubmitDisabled(authType, email, password);
  };

  handleSubmitAndRedirect = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (this.isSubmitDisabled()) {
      const { email, password, handleInputErrors, authType } = this.props;
      handleInputErrors(email, password, authType);
    } else {
      this.setState({ isSubmitting: true });
      this.props.handleOnSubmit(e);
    }
  };

  getFooterText = () => {
    if (this.props.authType === 'signup') {
      return (
        <Text textAlign="center" color="#A9A9A9" component="p" variant="body">
          By creating an account, you agree to our&nbsp;
          <StyledLink variant="info" target="_blank" href="https://withjoy.com/EULA/">
            terms of service
          </StyledLink>
          &nbsp; and our&nbsp;
          <StyledLink variant="info" target="_blank" href="https://withjoy.com/privacy">
            privacy policy
          </StyledLink>
        </Text>
      );
    } else {
      return <Link onClick={() => this.props.handleFormChange('forgotPassword')}>I forgot my login</Link>;
    }
  };

  render() {
    const { email, password, authType, handleOnInput, handleOAuth, error } = this.props;
    const { isSubmitting } = this.state;
    const authenticationProvider = this.authText[authType];

    return (
      <EnhancedStack spacing="md">
        <Text textAlign="center" component="h1" variant="page">
          {authenticationProvider.heroText}
        </Text>
        {authenticationProvider.helpText()}
        <form className={styles.form} onSubmit={this.handleSubmitAndRedirect}>
          <InputStack spacing="sm">
            <Input
              onChange={handleOnInput}
              value={email.value}
              handleFocus={this.handleFocus}
              handleBlur={this.handleBlur}
              iconType="mail"
              placeholder={authenticationProvider.emailText}
              dataInput={'email'}
              type="email"
              label="Email"
              error={email.error}
              errorText={email.errorText}
            />
            <Input
              onChange={handleOnInput}
              value={password.value}
              handleFocus={this.handleFocus}
              handleBlur={this.handleBlur}
              iconType="lock"
              placeholder={authenticationProvider.passwordText}
              dataInput={'password'}
              type="password"
              label="Password"
              error={password.error}
              errorText={password.errorText}
            />
          </InputStack>
          <p className={styles.formError}>{error}</p>
          <Button fill={true} disabled={isSubmitting} marginTop="2rem" type="submit">
            {authenticationProvider.buttonText}
          </Button>
        </form>
        <Text color="#a9a9a9" component="p" variant="body">
          Or continue using
        </Text>
        <Stack vertical={false} spacing="sm">
          <AuthButton authType={authType} providerType="facebook" onClick={handleOAuth} />
          {authType === 'login' ? <AuthButton authType={authType} providerType="google" onClick={handleOAuth} /> : null}
        </Stack>
        {this.getFooterText()}
      </EnhancedStack>
    );
  }
}
